import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/lib/api";
import { MediaCard } from "@/components/MediaCard";
import { SendSongDialog } from "@/components/SendSongDialog";
import { useMe } from "@/context/MeContext";

const uniq = (arr) => [...new Set(arr)].sort((a, b) => a.localeCompare(b));

const Filter = ({ id, label, value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={`filter-${id}`} className="w-full border-white/10 bg-[#12121A] sm:w-44"><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent className="border-white/10 bg-[#181824] text-slate-200">
      <SelectItem value="all">{label}: todos</SelectItem>
      {options.map((o) => <SelectItem key={o} value={String(o)}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function Directory() {
  const [media, setMedia] = useState([]);
  const [f, setF] = useState({ genre: "all", country: "all", format: "all", cost: "all" });
  const [target, setTarget] = useState(null);
  const { isLoaded, isSignedIn } = useAuth();
  const { me } = useMe();
  const navigate = useNavigate();

  useEffect(() => { axios.get(`${API}/media`).then((r) => setMedia(r.data)).catch(() => {}); }, []);

  const opts = useMemo(() => ({
    genre: uniq(media.flatMap((m) => m.genres || [])),
    country: uniq(media.map((m) => m.country).filter(Boolean)),
    format: uniq(media.map((m) => m.format).filter(Boolean)),
    cost: ["1", "2", "3"],
  }), [media]);

  const shown = media.filter((m) =>
    (f.genre === "all" || m.genres?.includes(f.genre)) && (f.country === "all" || m.country === f.country) &&
    (f.format === "all" || m.format === f.format) && (f.cost === "all" || String(m.cost_credits) === f.cost));

  const onSend = (m) => {
    if (!isLoaded) return;
    if (!isSignedIn) { navigate("/sign-up"); return; }
    if (me && me.role === "curator") { toast.info("Solo los artistas pueden enviar canciones"); return; }
    setTarget(m);
  };

  return (
    <div data-testid="directory-page" className="mx-auto max-w-[1400px] px-4 py-16 md:px-8">
      <p className="font-mono text-xs tracking-widest text-amber-500/90">DIRECTORIO</p>
      <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">Radios, podcasts y blogs</h1>
      <p className="mt-4 max-w-xl text-slate-400">Envía tu canción a medios que escuchan tu género. Responden en máximo 7 días o recuperas tus créditos.</p>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Filter id="genre" label="Género" value={f.genre} onChange={(v) => setF({ ...f, genre: v })} options={opts.genre} />
        <Filter id="country" label="País" value={f.country} onChange={(v) => setF({ ...f, country: v })} options={opts.country} />
        <Filter id="format" label="Formato" value={f.format} onChange={(v) => setF({ ...f, format: v })} options={opts.format} />
        <Filter id="cost" label="Créditos" value={f.cost} onChange={(v) => setF({ ...f, cost: v })} options={opts.cost} />
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p data-testid="directory-count" className="font-mono text-xs text-slate-500">{shown.length} MEDIOS</p>
        <Link to="/suma-tu-medio" data-testid="directory-join-media" className="text-sm text-[#FF7A45] hover:underline">¿Tienes una radio, podcast o blog? Suma tu medio →</Link>
      </div>
      <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {shown.map((m) => <MediaCard key={m.id} m={m} onSend={onSend} />)}
      </div>
      {shown.length === 0 && <p className="mt-10 text-slate-500">Ningún medio coincide con esos filtros.</p>}
      <SendSongDialog media={target} onClose={() => setTarget(null)} />
    </div>
  );
}
